import React, {useEffect, useState} from "react";
import {Box, ButtonBase, Collapse, MenuItem, Slider, Stack, TextField, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowDown01Icon, Settings02Icon} from "@hugeicons/core-free-icons";
import {VocabularyRepository} from "../../../repositories/VocabularyRepository";

// No override: every field empty / null, so the preview uses the flow's current voice configuration
export const EMPTY_VOICE_OVERRIDES = {voiceModelId: "", voice: "", languageCode: "", speakingRate: null, pitch: null, prompt: ""};

const hasOverrides = (overrides) => Object.values(overrides).some(value => value !== "" && value !== null);

const fieldSx = {"& .MuiOutlinedInput-root": {borderRadius: "10px", bgcolor: "#fff", fontSize: 13}, "& .MuiInputLabel-root": {fontSize: 13}};

const SliderField = ({label, value, fallback, min, max, step, disabled, onChange}) => (
  <Box sx={{opacity: disabled ? 0.5 : 1}}>
    <Stack direction={"row"} justifyContent={"space-between"}>
      <Typography sx={{fontSize: 12, fontWeight: 600, color: "#6B6B80"}}>{label}</Typography>
      <Typography sx={{fontSize: 12, color: value === null ? "#8A8AA0" : "#4B55C9", fontVariantNumeric: "tabular-nums"}}>
        {value === null ? `${fallback ?? "default"} (current)` : Number(value).toFixed(2)}
      </Typography>
    </Stack>
    <Slider size={"small"} disabled={disabled} min={min} max={max} step={step}
            value={value ?? (typeof fallback === "number" ? fallback : (min + max) / 2)}
            onChange={(e, next) => onChange(next)}
            sx={{color: "#8491F7", py: 1}}/>
  </Box>
);

// Collapsible voice settings for the previews: try another voice model, or other voice settings of a Google model,
// without changing the flow. Empty fields keep the flow's current configuration (shown in the summary).
// overrides / onChange: the values sent to /api/vocabulary/audio.
export const VoiceSettings = ({subProjectId, overrides, onChange}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null);

  useEffect(() => {
    setOptions(null);
    onChange(EMPTY_VOICE_OVERRIDES);
    if (!subProjectId) return;
    let cancelled = false;
    VocabularyRepository.getVoiceOptions(subProjectId)
      .then(response => !cancelled && setOptions(response?.data || {}))
      .catch(e => {
        console.error(e);
        if (!cancelled) setOptions({});
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subProjectId]);

  const current = options?.current || {};
  const models = options?.models || [];
  const selectedModel = overrides.voiceModelId
    ? models.find(model => String(model.voiceModelId) === String(overrides.voiceModelId))
    : models.find(model => model.current) || (current.voiceModelId ? {google: current.google} : null);
  const isGoogle = Boolean(selectedModel ? selectedModel.google : current.google);
  const set = (key) => (value) => onChange({...overrides, [key]: value});
  const changed = hasOverrides(overrides);

  const summary = [
    overrides.voiceModelId ? selectedModel?.name : current.voiceModelName || "Default voice",
    overrides.voice || current.voice,
    overrides.languageCode || current.languageCode,
  ].filter(Boolean).join(" · ");

  return (
    <Box sx={{mt: 1.5, borderRadius: "12px", border: "1px solid #F0F0F6", bgcolor: "#FAFAFD"}}>
      <ButtonBase onClick={() => setOpen(value => !value)} sx={{
        width: "100%", justifyContent: "flex-start", gap: 1, px: 1.5, py: 1.25, borderRadius: "12px", textAlign: "left",
      }}>
        <Box sx={{display: "flex", color: changed ? "#4B55C9" : "#8A8AA0"}}><HugeiconsIcon icon={Settings02Icon} size={16}/></Box>
        <Typography sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}}>Voice settings</Typography>
        <Typography noWrap sx={{flex: 1, minWidth: 0, fontSize: 12, color: changed ? "#4B55C9" : "#8A8AA0"}}>
          {options === null ? "Loading…" : `${changed ? "Trying" : "Current"}: ${summary}`}
        </Typography>
        <Box sx={{display: "flex", color: "#8A8AA0", transition: "transform 0.15s", transform: open ? "rotate(180deg)" : "none"}}>
          <HugeiconsIcon icon={ArrowDown01Icon} size={16}/>
        </Box>
      </ButtonBase>

      <Collapse in={open}>
        <Box sx={{px: 1.5, pb: 1.75}}>
          <Typography sx={{mb: 1.5, fontSize: 12, color: "#8A8AA0"}}>
            Applies to every preview on this page only - the flow's voice is not changed. Empty fields keep the current setting.
          </Typography>
          <Box sx={{display: "grid", gap: 1.5, gridTemplateColumns: {xs: "1fr", md: "repeat(3, minmax(0, 1fr))"}}}>
            <TextField
              select size={"small"} label={"Voice model"} sx={fieldSx}
              value={overrides.voiceModelId}
              // a different model has its own voices, so the Google-only fields start over
              onChange={(e) => onChange({...EMPTY_VOICE_OVERRIDES, languageCode: overrides.languageCode, voiceModelId: e.target.value})}
              SelectProps={{displayEmpty: true}}
              InputLabelProps={{shrink: true}}
            >
              <MenuItem value={""}>
                <Typography sx={{fontSize: 13}}>Current{current.voiceModelName ? ` (${current.voiceModelName})` : ""}</Typography>
              </MenuItem>
              {models.filter(model => !model.current).map(model => (
                <MenuItem key={model.voiceModelId} value={String(model.voiceModelId)}>
                  <Stack>
                    <Typography sx={{fontSize: 13}}>{model.name}</Typography>
                    <Typography sx={{fontSize: 11, color: "#8A8AA0"}}>
                      {[model.provider, model.modelId].filter(Boolean).join(" · ")}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size={"small"} label={"Voice"} sx={fieldSx} disabled={!isGoogle}
              placeholder={current.voice || "e.g. Charon"}
              helperText={isGoogle ? "Google / Gemini voice name" : "Google models only"}
              value={overrides.voice} onChange={(e) => set("voice")(e.target.value)}
              InputLabelProps={{shrink: true}}
            />
            <TextField
              size={"small"} label={"Language code"} sx={fieldSx}
              placeholder={current.languageCode || "e.g. mk-MK"}
              value={overrides.languageCode} onChange={(e) => set("languageCode")(e.target.value)}
              InputLabelProps={{shrink: true}}
            />
            <SliderField label={"Speaking rate"} value={overrides.speakingRate} fallback={current.speakingRate}
                         min={0.25} max={2} step={0.05} disabled={!isGoogle} onChange={set("speakingRate")}/>
            <SliderField label={"Pitch"} value={overrides.pitch} fallback={current.pitch}
                         min={-20} max={20} step={0.5} disabled={!isGoogle} onChange={set("pitch")}/>
            <TextField
              size={"small"} label={"Style prompt"} sx={fieldSx} disabled={!isGoogle}
              placeholder={"e.g. Speak calmly and clearly"}
              helperText={isGoogle ? "Instructions for Gemini voices" : "Google models only"}
              value={overrides.prompt} onChange={(e) => set("prompt")(e.target.value)}
              InputLabelProps={{shrink: true}}
            />
          </Box>
          {changed && (
            <ButtonBase onClick={() => onChange(EMPTY_VOICE_OVERRIDES)} sx={{
              mt: 1, px: 1, py: 0.5, borderRadius: "8px", fontSize: 12, fontWeight: 600, color: "#4B55C9",
              "&:hover": {bgcolor: "rgba(132, 145, 247, 0.12)"},
            }}>
              Reset to current voice
            </ButtonBase>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// Overrides as /api/vocabulary/audio parameters; the Google-only ones are dropped for other models
export const toAudioOptions = (overrides, isGoogleModel = true) => ({
  voiceModelId: overrides.voiceModelId || undefined,
  languageCode: overrides.languageCode?.trim() || undefined,
  ...(isGoogleModel ? {
    voice: overrides.voice?.trim() || undefined,
    speakingRate: overrides.speakingRate ?? undefined,
    pitch: overrides.pitch ?? undefined,
    prompt: overrides.prompt?.trim() || undefined,
  } : {}),
});
